import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useApproveProduct, usePatchProduct } from "../../../../api/products";
import useFormImageUpload from "../../../../hooks/formHooks/useFormImageUpload";
import useNotify from "../../../../hooks/useNotify";
import { PRODUCT_CATEGORY, ProductRequest } from "../../../../models/Product";
import { requiredForSubmit } from "../../../../pages/reward-inventory-portal/create-product/CreateProduct.const";
import { rootPaths } from "../../../../routes/routePaths";
import Text from "../../../atoms/Text";
import ExclamationIcon from "../../../icons/ui/ExclamationIcon";

const submitHelperText =
  "Please ensure that you have completed the entire form. This includes vouchers and images";

const ButtonSubmit = ({
  productId,
  label = "Submit",
  productName,
  ...actions
}: {
  productId: string;
  label?: string;
  productName?: string;
}) => {
  const { mutateAsync: patchProductAsync } = usePatchProduct();
  const { mutateAsync: patchSubmitProductAsync } = useApproveProduct();
  const { setNotifySuccess, setNotifyError } = useNotify();
  const formContext = useFormContext<ProductRequest>();
  const [isLoading, setIsLoading] = useState(false);
  const handleImageUpload = useFormImageUpload();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const valuesRequiredForSubmit = formContext.watch(requiredForSubmit);

  const messageErrorRequired = {
    message: "This field is required",
    type: "required",
  };

  const isHaveAuction =
    formContext.watch("productDetails.productCategory") ===
    PRODUCT_CATEGORY.AUCTION;

  const canSubmit =
    valuesRequiredForSubmit.every(item =>
      typeof item === "number" ? item >= 0 : !!item,
    ) && !formContext.formState.errors.productDetails;

  const handleSubmit = async () => {
    try {
      if (!productId) {
        throw new Error("Submitting without product id");
      }

      setIsLoading(true);

      if (handleImageUpload) {
        await handleImageUpload();
      }

      const formData = formContext.getValues();

      const productRequestConvert = {
        ...formData,
        productDetails: {
          ...formData.productDetails,
          userLimitFrequency:
            formData.productDetails.userLimitFrequency || undefined,
          userLimitNumber: formData.productDetails.userLimitNumber || undefined,
          voucherLimitFrequency:
            formData.productDetails.voucherLimitFrequency || undefined,
          voucherLimitNumber:
            formData.productDetails.voucherLimitNumber || undefined,
        },
      };
      if (
        formContext.getValues("productDetails.productCategory") ===
          PRODUCT_CATEGORY.AUCTION &&
        !formContext.getValues("productDetails.auctionType")
      ) {
        formContext.setError(
          "productDetails.auctionType",
          messageErrorRequired,
        );
      } else {
        formContext.clearErrors("productDetails.auctionType");
      }
      if (!productRequestConvert.productDetails.auctionType) {
        delete productRequestConvert.productDetails.auctionType;
      }

      if (formContext.formState.errors.productDetails) {
        throw new Error("Required fields must be completed to save");
      }

      await patchProductAsync({
        productId,
        productRequest: productRequestConvert,
      });
      await patchSubmitProductAsync({
        productId,
      });

      navigate(rootPaths.rewardInventoryPortal);
      setNotifySuccess("You have successfully submitted the product");
    } catch (error) {
      // Need to notify the UI
      if (error instanceof Error) {
        setNotifyError(error.message);
      } else {
        setNotifyError("Oops! something went wrong.");
      }
    }

    setIsLoading(false);
  };

  return (
    <Tooltip title={!canSubmit && submitHelperText}>
      <span>
        <Button
          {...actions}
          onClick={handleOpenModal}
          variant="primary"
          size="small"
          sx={{ width: 120 }}
          disabled={isLoading || !canSubmit}
          startIcon={isLoading && <CircularProgress size={20} />}
        >
          {label}
        </Button>
        <Modal
          open={isModalOpen}
          onClose={handleCloseModal}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          data-testid="modal-component"
        >
          <Box
            width={532}
            bgcolor="mono.white"
            borderRadius={2.5}
            pb={3}
            overflow="hidden"
          >
            <Box px={2.5} py={2} bgcolor="mono.light.grey">
              <Text variant={"title_medium"}>Confirm Submission</Text>
            </Box>

            <Stack p={3} alignItems="center">
              <ExclamationIcon width={48} height={48} />

              <Stack mt={2} mb={4} alignItems="center">
                <Text variant={"body_large"}>
                  Are you sure you want to submit?
                </Text>
                <Text variant={"title_small"}>{productId}</Text>
                {productName && (
                  <Text variant={"title_small"}>{productName}</Text>
                )}
              </Stack>

              <Stack direction="row" spacing={1} width={256}>
                <Button
                  variant="secondary"
                  size="small"
                  fullWidth={true}
                  onClick={handleCloseModal}
                  data-testid="close-modal-button"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  fullWidth={true}
                  disabled={isLoading}
                  startIcon={isLoading && <CircularProgress size={20} />}
                  onClick={handleSubmit}
                  data-testid="delete-button"
                >
                  Confirm
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal>
      </span>
    </Tooltip>
  );
};

export default ButtonSubmit;
